import React from 'react';
import { ButtonV2, Flex } from '@withjoy/joykit';
import type { HandleSendEmailArgs } from './Confirm.controller';
import { useConfirmController } from './Confirm.controller';
import { ConfirmContainer, ConfirmWrapper, StyledActionWrapper, StyledTitle, StyledBodyTitle, StyledBody, styles } from './Confirm.styles';
import SendTestEmailDialog from './components/SendTestEmailDailog';
import type { EcardType, PostOfficeMessageType } from '@graphql/generated';
import { notNullOrUndefined } from '@shared/utils/notNullOrUndefined';
import { Copy } from '@withjoy/joykit/icons';
import ShareableDialog from './components/ShareableDialog';
import { useGetValidReplyToAddresses } from '../EmailEditor/components/EmailEditPanel/hooks/getValidReplyToAddresses';

export type SuccessScreen = 'generic' | 'thankyou';

interface ConfirmProps
  extends Readonly<{
    eventHandle: string;
    handleSendEmail: (args: HandleSendEmailArgs) => void;
    emailIsVerified: boolean;
    draftType: EcardType | PostOfficeMessageType;
    testEmail?: string;
    hideGuestSelector?: boolean;
    generateShareableLink?: () => Promise<string | null>;
    handleCopyShareableLink?: (generatedLink: string) => void;
    aboutShareableLinksClick?: (label: 'Cancel' | 'Copy Link') => void;
    confirmSelectGuestsTrack?: (ecardType: EcardType) => void;
    confirmTestEmailTrack?: (ecardType: EcardType) => void;
    confirmAndSendTrack?: (ecardType: EcardType) => void;
  }> {}

export const Confirm: React.FC<ConfirmProps> = ({
  eventHandle,
  handleSendEmail,
  emailIsVerified,
  draftType,
  testEmail,
  hideGuestSelector,
  handleCopyShareableLink,
  generateShareableLink,
  aboutShareableLinksClick,
  confirmSelectGuestsTrack,
  confirmTestEmailTrack,
  confirmAndSendTrack,
}) => {
  const { confirmAndSendActions, tConfirmAndSend, handleSendTestEmail, handleSendTestEmailDialogClose, isSendTestEmailDialogOpen, shareableDialog } = useConfirmController({
    eventHandle,
    handleSendEmail,
    emailIsVerified,
    draftType,
    hideGuestSelector,
    confirmSelectGuestsTrack,
    confirmTestEmailTrack,
    confirmAndSendTrack,
  });

  const allowedTestEmailRecipients = useGetValidReplyToAddresses(eventHandle);

  return (
    <ConfirmContainer
      alignItems="center"
      justifyContent="center"
      __css={styles.confirmContainer}
    >
      <ConfirmWrapper
        flexDirection="column"
        __css={styles.confirmWrapper}
      >
        {!handleCopyShareableLink && (
          <>
            <StyledTitle typographyVariant="hed5">{tConfirmAndSend.header()}</StyledTitle>
            <StyledBody
              marginBottom={4}
              typographyVariant="body1"
            >
              {tConfirmAndSend.body()}
            </StyledBody>
          </>
        )}
        {handleCopyShareableLink && (
          <Flex
            flexDirection="column"
            alignItems="center"
          >
            <img
              src="https://withjoy.com/assets/public/social_icons_big.png"
              alt="Social"
              style={{ height: '24px', width: '184px' }}
            />
            <StyledTitle
              marginTop={5}
              typographyVariant="hed5"
              textAlign="center"
            >
              {tConfirmAndSend.shareTitle()}
            </StyledTitle>
            <StyledBody
              marginBottom={6}
              marginTop={3}
              typographyVariant="body1"
              textAlign="center"
            >
              {tConfirmAndSend.shareBody()}
            </StyledBody>
            <ButtonV2
              onClick={shareableDialog.onOpen}
              marginBottom={8}
              marginX="auto"
              maxWidth="212px"
              width="100%"
              variant="outline"
            >
              <Copy marginRight={3} />
              {tConfirmAndSend.shareAction()}
            </ButtonV2>
            <StyledTitle
              typographyVariant="hed5"
              textAlign="center"
            >
              {tConfirmAndSend.emailHeader()}
            </StyledTitle>
            <StyledBody
              marginBottom={6}
              marginTop={3}
              marginX="auto"
              typographyVariant="body1"
              textAlign="center"
              maxWidth="490px"
            >
              {tConfirmAndSend.emailBody()}
            </StyledBody>
            <ShareableDialog
              isOpen={shareableDialog.isOpen}
              onClose={shareableDialog.onClose}
              onCopy={handleCopyShareableLink}
              generateShareableLink={generateShareableLink}
              onCancelClick={() => aboutShareableLinksClick?.('Cancel')}
              onConfirmClick={() => aboutShareableLinksClick?.('Copy Link')}
            />
          </Flex>
        )}
        {confirmAndSendActions.filter(notNullOrUndefined).map(action => (
          <StyledActionWrapper
            key={action.label}
            alignItems="center"
            justifyContent="space-between"
            __css={styles.actionWrapper}
          >
            <Flex>
              {action.icon}
              <Flex
                flexDirection="column"
                marginLeft={6}
              >
                <StyledBodyTitle
                  marginBottom={0}
                  typographyVariant="hed2"
                >
                  {action.hed}
                </StyledBodyTitle>
                <StyledBody typographyVariant="body1">{action.dek}</StyledBody>
              </Flex>
            </Flex>
            {action.button}
          </StyledActionWrapper>
        ))}
      </ConfirmWrapper>
      <SendTestEmailDialog
        dialogId="send-test-email-dialog-ecards"
        allowedEmailAddresses={allowedTestEmailRecipients}
        isOpen={isSendTestEmailDialogOpen}
        onClose={handleSendTestEmailDialogClose}
        handleSendTestEmail={handleSendTestEmail}
        testEmail={testEmail}
      />
    </ConfirmContainer>
  );
};
